import {
  Box as Flex,
  Chip as Tag,
  Typography as Text,
} from '@material-ui/core';

import { extractLastNameComponent } from '@spotify/confidence-core-react';
import {
  GetWorkflowInstanceQuery,
  getTypeOrNull,
} from '@spotify/confidence-graphql';
import { useSetResolvedTitle } from '@spotify/plugin-confidence-projects-react';
import {
  parseWorkflowInstanceName,
  useGetWorkflowInstance,
} from '@spotify/plugin-confidence-workflows-react';

import { PreviewShell, StateTag, SummarySection } from './components';

type WorkflowInstance = Extract<
  NonNullable<GetWorkflowInstanceQuery['workflowInstance']>,
  { __typename: 'WorkflowV1WorkflowInstance' }
>;

type ModuleData = WorkflowInstance['moduleData2'];
type AbTestModule = NonNullable<ModuleData['abtest']>;
type FlagsModule = NonNullable<ModuleData['flags']>;
type MetricsModule = NonNullable<ModuleData['metrics']>;

const entityLabel = (entity: { name: string; displayName?: string | null }) =>
  entity.displayName || extractLastNameComponent(entity.name) || entity.name;

const formatWeight = (weight: unknown) => {
  if (typeof weight === 'number') return `${weight}%`;
  if (typeof weight === 'string' && weight.length > 0) return `${weight}%`;
  return '—';
};

const TreatmentList = ({ abtest }: { abtest: AbTestModule }) => {
  const treatments = abtest.treatments ?? [];
  if (treatments.length === 0) {
    return (
      <Text variant="body2" color="textSecondary">
        No treatments.
      </Text>
    );
  }
  return (
    <Flex
      display="flex"
      flexDirection="column"
      style={{
        gap: 'var(--bui-space-1)',
      }}
    >
      {treatments.map((treatment, index) => {
        const variantName = getTypeOrNull(treatment.variant)?.name;
        const label =
          treatment.displayName ||
          treatment.name ||
          (variantName ? extractLastNameComponent(variantName) : undefined) ||
          `Treatment ${index + 1}`;
        return (
          <Flex
            display="flex"
            key={`${label}-${index}`}
            alignItems="center"
            style={{
              gap: 'var(--bui-space-2)',
            }}
          >
            <Text variant="body2">{label}</Text>
            <Text variant="body2" color="textSecondary">
              {formatWeight(treatment.weight)}
            </Text>
          </Flex>
        );
      })}
    </Flex>
  );
};

const AudienceSummary = ({ flags }: { flags: FlagsModule }) => {
  const segment = getTypeOrNull(flags.segment);
  const proportionRaw = segment?.allocation?.proportion?.value;
  const proportionNumber = proportionRaw ? Number(proportionRaw) : undefined;
  const allocationPercent =
    proportionNumber !== undefined && Number.isFinite(proportionNumber)
      ? `${Math.round(proportionNumber * 100)}%`
      : undefined;
  const criteriaCount = segment?.targeting?.criteria?.length ?? 0;

  return (
    <Flex
      display="flex"
      flexDirection="column"
      style={{
        gap: 'var(--bui-space-2)',
      }}
    >
      {flags.targetingKeySelector && (
        <Flex
          display="flex"
          alignItems="center"
          style={{
            gap: 'var(--bui-space-2)',
          }}
        >
          <Text variant="body2">Randomized by</Text>
          <Flex
            display="flex"
            flexWrap="wrap"
            style={{
              gap: 'var(--bui-space-2)',
            }}
            aria-label="Randomization key"
          >
            <Tag
              id={flags.targetingKeySelector}
              label={flags.targetingKeySelector}
              size="small"
            />
          </Flex>
        </Flex>
      )}
      {allocationPercent && (
        <Flex
          display="flex"
          alignItems="center"
          style={{
            gap: 'var(--bui-space-2)',
          }}
        >
          <Text variant="body2">Allocation:</Text>
          <Text variant="body2" color="textSecondary">
            {allocationPercent}
          </Text>
        </Flex>
      )}
      <Text variant="body2" color="textSecondary">
        {criteriaCount === 0
          ? 'All users'
          : `${criteriaCount} user criteri${criteriaCount === 1 ? 'on' : 'a'}`}
      </Text>
    </Flex>
  );
};

const isSuccessRole = (
  metricRole: MetricsModule['metrics'][number]['metricRole'],
): boolean => 'successMetricKind' in metricRole;

const MetricList = ({
  title,
  metrics,
}: {
  title: string;
  metrics: ReadonlyArray<MetricsModule['metrics'][number]>;
}) => {
  if (metrics.length === 0) {
    return null;
  }
  return (
    <SummarySection title={title}>
      <Flex
        display="flex"
        flexWrap="wrap"
        style={{
          gap: 'var(--bui-space-2)',
        }}
        aria-label={title}
      >
        {metrics.map((entry, index) => {
          const metric = getTypeOrNull(entry.metric);
          const label =
            metric?.displayName ||
            (metric?.name
              ? extractLastNameComponent(metric.name)
              : undefined) ||
            `Metric ${index + 1}`;
          const id = metric?.name ?? `metric-${index}`;
          return <Tag key={id} id={id} label={label} size="small" />;
        })}
      </Flex>
    </SummarySection>
  );
};

interface WorkflowArtifactPreviewProps {
  resourceName: string;
  label: string;
}

export const WorkflowArtifactPreview = ({
  resourceName,
  label,
}: WorkflowArtifactPreviewProps) => {
  const { workflow, id } = parseWorkflowInstanceName(resourceName);
  const { workflowInstance, loading, errorMessage } = useGetWorkflowInstance({
    workflowId: workflow,
    instanceId: id,
  });

  const previewError =
    errorMessage ?? (!loading && !workflowInstance ? 'not-found' : undefined);

  useSetResolvedTitle(
    workflowInstance?.displayName || extractLastNameComponent(resourceName),
  );

  const moduleData = workflowInstance?.moduleData2;
  const abtest = moduleData?.abtest ?? null;
  const flagsModule = moduleData?.flags ?? null;
  const metricsModule = moduleData?.metrics ?? null;
  const hypothesis = moduleData?.hypothesis?.text ?? '';
  const entity = getTypeOrNull(metricsModule?.entity);
  const assignmentTable = getTypeOrNull(metricsModule?.assignmentTable);
  const surfaces = (workflowInstance?.surfaces ?? [])
    .map(surface => getTypeOrNull(surface))
    .filter((surface): surface is NonNullable<typeof surface> =>
      Boolean(surface),
    );
  const successMetrics = (metricsModule?.metrics ?? []).filter(m =>
    isSuccessRole(m.metricRole),
  );
  const guardrailMetrics = (metricsModule?.metrics ?? []).filter(
    m => !isSuccessRole(m.metricRole),
  );

  return (
    <PreviewShell loading={loading} error={previewError}>
      {workflowInstance && (
        <>
          <Flex
            display="flex"
            alignItems="center"
            style={{
              gap: 'var(--bui-space-2)',
            }}
          >
            <Text variant="body2" color="textSecondary" component="span">
              {label}
            </Text>
            <StateTag state={workflowInstance.state} />
          </Flex>
          {hypothesis && (
            <SummarySection title="Hypothesis">
              <Text variant="body2">{hypothesis}</Text>
            </SummarySection>
          )}
          {entity && (
            <SummarySection title="Entity">
              <Flex
                display="flex"
                flexWrap="wrap"
                style={{
                  gap: 'var(--bui-space-2)',
                }}
                aria-label="Entity"
              >
                <Tag
                  id={entity.name}
                  label={entityLabel(entity)}
                  size="small"
                />
              </Flex>
            </SummarySection>
          )}
          {assignmentTable && (
            <SummarySection title="Assignment table">
              <Flex
                display="flex"
                flexWrap="wrap"
                style={{
                  gap: 'var(--bui-space-2)',
                }}
                aria-label="Assignment table"
              >
                <Tag
                  id={assignmentTable.name}
                  label={entityLabel(assignmentTable)}
                  size="small"
                />
              </Flex>
            </SummarySection>
          )}
          {surfaces.length > 0 && (
            <SummarySection
              title={surfaces.length === 1 ? 'Surface' : 'Surfaces'}
            >
              <Flex
                display="flex"
                flexWrap="wrap"
                style={{
                  gap: 'var(--bui-space-2)',
                }}
                aria-label="Surfaces"
              >
                {surfaces.map(surface => (
                  <Tag
                    key={surface.name}
                    id={surface.name}
                    label={surface.displayName}
                    size="small"
                  />
                ))}
              </Flex>
            </SummarySection>
          )}
          {abtest && (
            <SummarySection title="Treatments">
              <TreatmentList abtest={abtest} />
            </SummarySection>
          )}
          {flagsModule && (
            <SummarySection title="Audience">
              <AudienceSummary flags={flagsModule} />
            </SummarySection>
          )}
          <MetricList title="Success metrics" metrics={successMetrics} />
          <MetricList title="Guardrail metrics" metrics={guardrailMetrics} />
        </>
      )}
    </PreviewShell>
  );
};
